/**
 * Store Working Hours Edit
 *
 * Refactored to use SmartScheduleEditor for a consistent, compact grouped view.
 * Features store status indicator, visual feedback on changes, and bulk actions.
 *
 * @module components
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Schedule as ClockIcon,
  Store as StoreIcon,
  Public as GlobalIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import config from '../config';
import Logger from '../utils/Logger';
import { getValidToken } from '../services/tokenRefreshService';
import { SmartScheduleEditor, type ScheduleData, type DaySchedule } from '../pages/StoreCommandCenter/components/ui/SmartScheduleEditor';

// ============================================================================
// Types
// ============================================================================

interface StoreStatus {
  isOpen: boolean;
  currentDay: number;
  currentTime: string;
  message: string;
  nextChange: string;
  minutesUntilChange: number;
}

// ============================================================================
// Constants
// ============================================================================

const colors = {
  bgDeepest: 'var(--canvas)',
  bgCard: 'var(--panel)',
  bgElevated: 'var(--raised)',
  bgRecessed: 'var(--sunk)',
  textPrimary: 'var(--tx)',
  textSecondary: 'var(--tx2)',
  textMuted: 'var(--tx3)',
  border: 'var(--line-soft)',
  successGlow: '#00FF88',
  errorGlow: '#FF003C',
  warning: '#FFB800',
  info: '#00B4FF',
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate current store status based on schedule
 */
function calculateStoreStatus(schedule: ScheduleData, useGlobalDefaults: boolean, globalSchedule: ScheduleData): StoreStatus {
  const now = new Date();
  const currentDay = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

  const activeSchedule = useGlobalDefaults ? globalSchedule : schedule;
  const todaySchedule = activeSchedule[currentDay];

  if (!todaySchedule || !todaySchedule.openTime || !todaySchedule.closeTime) {
    return {
      isOpen: false,
      currentDay,
      currentTime: currentTimeStr,
      message: 'No hours configured for today',
      nextChange: 'Configure hours to see status',
      minutesUntilChange: 0,
    };
  }

  const openMinutes = timeToMinutes(todaySchedule.openTime);
  const closeMinutes = timeToMinutes(todaySchedule.closeTime);
  const currentMinutes = currentHour * 60 + currentMinute;

  const isOpen = currentMinutes >= openMinutes && currentMinutes < closeMinutes;

  let message: string;
  let nextChange: string;
  let minutesUntilChange: number;

  if (isOpen) {
    minutesUntilChange = closeMinutes - currentMinutes;
    const hoursUntil = Math.floor(minutesUntilChange / 60);
    const minsUntil = minutesUntilChange % 60;

    if (hoursUntil > 0) {
      message = `Open now - closes in ${hoursUntil}h ${minsUntil}m`;
    } else {
      message = `Open now - closes in ${minsUntil}m`;
    }
    nextChange = `Will close at ${todaySchedule.closeTime}`;
  } else {
    if (currentMinutes < openMinutes) {
      // Before opening
      minutesUntilChange = openMinutes - currentMinutes;
      const hoursUntil = Math.floor(minutesUntilChange / 60);
      const minsUntil = minutesUntilChange % 60;

      if (hoursUntil > 0) {
        message = `Closed - opens in ${hoursUntil}h ${minsUntil}m`;
      } else {
        message = `Closed - opens in ${minsUntil}m`;
      }
      nextChange = `Will open at ${todaySchedule.openTime}`;
    } else {
      // After closing - find next open day
      let nextOpenDay = (currentDay + 1) % 7;
      let daysAhead = 1;

      while (daysAhead <= 7) {
        const nextSchedule = activeSchedule[nextOpenDay];
        if (nextSchedule?.openTime && nextSchedule?.closeTime) {
          break;
        }
        nextOpenDay = (nextOpenDay + 1) % 7;
        daysAhead++;
      }

      minutesUntilChange = ((24 * 60) - currentMinutes) + timeToMinutes(activeSchedule[nextOpenDay]?.openTime || '08:00') + ((daysAhead - 1) * 24 * 60);
      const hoursUntil = Math.floor(minutesUntilChange / 60);

      message = `Closed now`;
      nextChange = `Opens ${DAYS[nextOpenDay]} at ${activeSchedule[nextOpenDay]?.openTime || '08:00'} (${hoursUntil}h from now)`;
    }
  }

  return {
    isOpen,
    currentDay,
    currentTime: currentTimeStr,
    message,
    nextChange,
    minutesUntilChange,
  };
}

function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Transform backend working hours to ScheduleData format
 */
function transformToScheduleData(workingHours: Array<{ dayOfWeek: number; openTime: string; closeTime: string }>): ScheduleData {
  const schedule: ScheduleData = {};

  for (let day = 0; day < 7; day++) {
    schedule[day] = {
      openTime: '',
      closeTime: '',
      liquorCutoff: null,
      liquorStartTime: null,
    };
  }

  for (const wh of workingHours) {
    if (schedule[wh.dayOfWeek]) {
      schedule[wh.dayOfWeek].openTime = wh.openTime?.substring(0, 5) || '';
      schedule[wh.dayOfWeek].closeTime = wh.closeTime?.substring(0, 5) || '';
    }
  }

  return schedule;
}

/**
 * Transform ScheduleData back to backend format
 */
function transformFromScheduleData(scheduleData: ScheduleData): Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }> {
  const workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }> = [];

  for (let day = 0; day < 7; day++) {
    const daySchedule = scheduleData[day];
    if (daySchedule?.openTime && daySchedule?.closeTime) {
      workingHours.push({
        dayOfWeek: day,
        slotNumber: 1,
        openTime: daySchedule.openTime.length === 5 ? `${daySchedule.openTime}:00` : daySchedule.openTime,
        closeTime: daySchedule.closeTime.length === 5 ? `${daySchedule.closeTime}:00` : daySchedule.closeTime,
      });
    }
  }

  return workingHours;
}

// ============================================================================
// Status Indicator Component
// ============================================================================

interface StoreStatusIndicatorProps {
  status: StoreStatus;
  useGlobalDefaults: boolean;
}

function StoreStatusIndicator({ status, useGlobalDefaults }: StoreStatusIndicatorProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        mb: 3,
        bgcolor: status.isOpen ? `${colors.successGlow}08` : `${colors.errorGlow}08`,
        borderRadius: 2,
        border: `1px solid ${status.isOpen ? `${colors.successGlow}40` : `${colors.errorGlow}40`}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: status.isOpen ? `${colors.successGlow}20` : `${colors.errorGlow}20`,
              border: `1px solid ${status.isOpen ? `${colors.successGlow}50` : `${colors.errorGlow}50`}`,
            }}
          >
            {status.isOpen ? (
              <CheckIcon sx={{ fontSize: 28, color: colors.successGlow }} />
            ) : (
              <TimeIcon sx={{ fontSize: 28, color: colors.errorGlow }} />
            )}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: colors.textPrimary }}>
                {status.isOpen ? 'Store is Open' : 'Store is Closed'}
              </Typography>
              {useGlobalDefaults && (
                <Chip
                  icon={<GlobalIcon sx={{ fontSize: 12 }} />}
                  label="Using Defaults"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: `${colors.info}15`,
                    color: colors.info,
                    border: `1px solid ${colors.info}30`,
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {status.message}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
            Current time: {status.currentTime}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: status.isOpen ? colors.errorGlow : colors.successGlow,
              fontWeight: 600,
            }}
          >
            {status.nextChange}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

// ============================================================================
// Change Preview Component
// ============================================================================

interface ChangePreviewProps {
  currentSchedule: ScheduleData;
  newSchedule: ScheduleData;
  useGlobalDefaults: boolean;
}

function ChangePreview({ currentSchedule, newSchedule, useGlobalDefaults }: ChangePreviewProps) {
  const changes = useMemo(() => {
    const diffs: Array<{ day: string; field: string; old: string; new: string }> = [];

    for (let day = 0; day < 7; day++) {
      const current = currentSchedule[day];
      const new_ = newSchedule[day];

      if (!current || !new_) continue;

      if (current.openTime !== new_.openTime) {
        diffs.push({
          day: DAYS[day],
          field: 'Opens',
          old: current.openTime || 'Not set',
          new: new_.openTime || 'Not set',
        });
      }

      if (current.closeTime !== new_.closeTime) {
        diffs.push({
          day: DAYS[day],
          field: 'Closes',
          old: current.closeTime || 'Not set',
          new: new_.closeTime || 'Not set',
        });
      }
    }

    return diffs;
  }, [currentSchedule, newSchedule]);

  if (changes.length === 0) return null;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        bgcolor: `${colors.warning}08`,
        borderRadius: 2,
        border: `1px solid ${colors.warning}40`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <WarningIcon sx={{ fontSize: 18, color: colors.warning }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.warning }}>
          Changes Preview
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {changes.slice(0, 5).map((change, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary, minWidth: 80 }}>
              {change.day}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted, minWidth: 50 }}>
              {change.field}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.errorGlow, textDecoration: 'line-through' }}>
              {change.old}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>→</Typography>
            <Typography variant="caption" sx={{ color: colors.successGlow, fontWeight: 600 }}>
              {change.new}
            </Typography>
          </Box>
        ))}
        {changes.length > 5 && (
          <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5 }}>
            ...and {changes.length - 5} more changes
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function StoreWorkingHoursEdit() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const [storeName, setStoreName] = useState('');

  // Schedule state
  const [schedule, setSchedule] = useState<ScheduleData>({});
  const [originalSchedule, setOriginalSchedule] = useState<ScheduleData>({});
  const [globalSchedule, setGlobalSchedule] = useState<ScheduleData>({});
  const [useGlobalDefaults, setUseGlobalDefaults] = useState(true);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Calculate store status
  const storeStatus = useMemo(() => {
    return calculateStoreStatus(schedule, useGlobalDefaults, globalSchedule);
  }, [schedule, useGlobalDefaults, globalSchedule]);

  useEffect(() => {
    fetchWorkingHours();
  }, [storeId]);

  const fetchWorkingHours = async () => {
    const token = await getValidToken();

    try {
      // Fetch store info
      const storeRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const storeData = await storeRes.json();
      setStoreName(storeData.store.name);

      // Fetch store-specific hours
      const storeHoursRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/working-hours`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const storeHoursData = await storeHoursRes.json();

      // Fetch system default hours
      const defaultsRes = await fetch(`${config.API_URL}/admin/stores/defaults`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const defaultsData = await defaultsRes.json();

      // Transform to ScheduleData format
      const storeSchedule = transformToScheduleData(storeHoursData || []);
      const defaultSchedule = transformToScheduleData(defaultsData.defaultWorkingHours || []);

      // Determine if using global defaults (no custom hours = using defaults)
      const hasCustomHours = storeHoursData && storeHoursData.length > 0;
      setUseGlobalDefaults(!hasCustomHours);

      // Set schedules
      setGlobalSchedule(defaultSchedule);
      setSchedule(hasCustomHours ? storeSchedule : defaultSchedule);
      setOriginalSchedule(hasCustomHours ? storeSchedule : defaultSchedule);

      Logger.log('StoreWorkingHoursEdit - Loaded:', {
        hasCustomHours,
        storeSchedule,
        defaultSchedule,
      });
    } catch (error) {
      Logger.error('Error fetching working hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = await getValidToken();

    try {
      let workingHours: Array<{ dayOfWeek: number; slotNumber: number; openTime: string; closeTime: string }>;

      if (useGlobalDefaults) {
        // Using global defaults - send empty array to clear custom hours
        workingHours = [];
      } else {
        // Using custom schedule
        workingHours = transformFromScheduleData(schedule);
      }

      Logger.log('=== SAVING WORKING HOURS ===');
      Logger.log('Using global defaults:', useGlobalDefaults);
      Logger.log('Saving hours:', workingHours);

      const response = await fetch(`${config.API_URL}/admin/stores/${storeId}/working-hours`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workingHours }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save working hours');
      }

      // Update original schedule after successful save
      setOriginalSchedule({ ...schedule });

      alert('Working hours saved successfully!');
    } catch (error) {
      Logger.error('Error saving working hours:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error saving working hours: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleManageDefaults = () => {
    navigate('/system-defaults');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          bgcolor: colors.bgDeepest,
        }}
      >
        <CircularProgress sx={{ color: colors.errorGlow }} />
      </Box>
    );
  }

  const hasChanges = JSON.stringify(schedule) !== JSON.stringify(originalSchedule) ||
    (useGlobalDefaults && Object.keys(originalSchedule).length > 0) ||
    (!useGlobalDefaults && Object.keys(originalSchedule).length === 0);

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor: colors.bgCard,
          borderRadius: 3,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${colors.info}15`,
                border: `1px solid ${colors.info}30`,
              }}
            >
              <StoreIcon sx={{ fontSize: 26, color: colors.info }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: colors.textPrimary, letterSpacing: '-0.02em' }}>
                {storeName}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Working Hours Configuration
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/stores')}
            sx={{
              color: colors.textSecondary,
              borderColor: colors.border,
              '&:hover': { borderColor: colors.textPrimary, color: colors.textPrimary },
            }}
          >
            Back to Stores
          </Button>
        </Box>
      </Paper>

      {/* Store Status Indicator */}
      <StoreStatusIndicator status={storeStatus} useGlobalDefaults={useGlobalDefaults} />

      {/* Info Alert */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          bgcolor: `${colors.info}08`,
          color: colors.textSecondary,
          border: `1px solid ${colors.info}20`,
          '& .MuiAlert-icon': { color: colors.info },
        }}
      >
        Configure store-specific working hours or use system defaults. Changes will affect when customers can place orders.
      </Alert>

      {/* Change Preview */}
      <ChangePreview
        currentSchedule={originalSchedule}
        newSchedule={schedule}
        useGlobalDefaults={useGlobalDefaults}
      />

      {/* Smart Schedule Editor */}
      <Paper
        sx={{
          p: 3,
          bgcolor: colors.bgCard,
          borderRadius: 3,
          border: `1px solid ${colors.border}`,
        }}
      >
        <SmartScheduleEditor
          schedule={schedule}
          globalSchedule={globalSchedule}
          useGlobalDefaults={useGlobalDefaults}
          onScheduleChange={setSchedule}
          onUseGlobalDefaultsChange={setUseGlobalDefaults}
          saving={saving}
          onManageDefaults={handleManageDefaults}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 3, borderTop: `1px solid ${colors.border}` }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/stores')}
              sx={{
                color: colors.textSecondary,
                borderColor: colors.border,
                '&:hover': { borderColor: colors.textPrimary, color: colors.textPrimary },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/stores/${storeId}/cutoffs`)}
              sx={{
                color: colors.textSecondary,
                borderColor: colors.border,
                '&:hover': { borderColor: colors.textPrimary, color: colors.textPrimary },
              }}
            >
              Manage Liquor Times
            </Button>
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
            sx={{
              bgcolor: colors.errorGlow,
              color: '#000',
              fontWeight: 600,
              '&:hover': { bgcolor: '#FF3366' },
              '&.Mui-disabled': { bgcolor: colors.border, color: colors.textMuted },
            }}
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default StoreWorkingHoursEdit;
